import { useCallback, useMemo } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import { generatePath } from 'react-router-dom';

import useBankingPath from '#client/hooks/useBankingPath';
import { useLogError } from '#client/hooks/useLogError';

import { useBankAccount } from './useBankAccount';
import { GET_BANKING_WIDGET } from '../queries/bankingWidget';

const TOTAL_SHOWN_TRANSACTIONS = 2;

export const useBankingWidget = () => {
  const { businessEntityUuid } = useBusinessEntity();

  /**
   * We need to get the bank account to get the unitAccountId first
   * to be able to fetch the banking widget data
   */
  const {
    data: bankAccountData,
    loading: bankAccountLoading,
    error: bankAccountError,
    refetch: refetchBankAccount
  } = useBankAccount();

  const unitAccountId = bankAccountData?.id;

  // Banking widget is enabled if the user has a bank account
  const hasBankAccount = !!unitAccountId;

  const {
    data: bankingWidgetData,
    loading: bankingWidgetLoading,
    error: widgetError,
    errors,
    refetch: refetchBankingWidget
  } = useQuery(GET_BANKING_WIDGET, {
    noThrow: true,
    useCache: false,
    variables: {
      input: {
        businessEntityUuid
      },
      businessEntityUuid,
      params: {
        filter: {
          businessEntityUuid
        },
        pagination: {
          limit: TOTAL_SHOWN_TRANSACTIONS,
          offset: 0
        }
      }
    },
    skip: !hasBankAccount
  });

  useLogError(errors, widgetError);

  const {
    bankCustomerAddress,
    transactions: bankTransactions,
    bankAccountStatus
  } = bankingWidgetData || {};

  const loading = bankAccountLoading || bankingWidgetLoading;

  const bankingWidgetError = errors?.[0];
  const error = bankAccountError || bankingWidgetError;

  const bankingPath = useBankingPath();

  // Refetch banking widget and bank account if there is an error on either
  const refetch = useCallback(() => {
    if (bankAccountError) {
      refetchBankAccount();
    }
    if (bankingWidgetError) {
      refetchBankingWidget();
    }
  }, [bankAccountError, bankingWidgetError, refetchBankAccount, refetchBankingWidget]);

  const accountDetails = useMemo(() => {
    const { accountNumber, routingNumber, name } = bankAccountData || {
      accountNumber: '',
      routingNumber: '',
      name: ''
    };

    const { address1, address2, city, state, zip } = bankCustomerAddress || {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: ''
    };

    const firstAddressLine = `${address1}${address2 ? ` ${address2}` : ''}`;
    const secondAddressLine = `${city}, ${state}, ${zip}`;

    return {
      accountNumber,
      routingNumber,
      name,
      firstAddressLine,
      secondAddressLine
    };
  }, [bankAccountData, bankCustomerAddress]);

  const availableBalance = bankAccountData?.availableBalance?.formattedAmount ?? '$0.00';

  // Transactions are shown in the banking widget
  const transactions = useMemo(() => bankTransactions?.data || [], [bankTransactions?.data]);

  const isEmptyTransaction = transactions.length === 0;

  const { accountHasLinkedBankAccount, accountHasPayment, accountHasTransaction } =
    bankAccountStatus || {};

  const addFundModalHref = useMemo(() => {
    const commonPath = `${bankingPath}/dashboard?modal=`;
    // If user has linked bank account or not, no payment and no transaction, show add funds button
    const isEligibleToLinkBankAccount =
      !accountHasLinkedBankAccount && !accountHasPayment && !accountHasTransaction;
    const isEligibleToAddFunds =
      accountHasLinkedBankAccount && !accountHasPayment && !accountHasTransaction;
    if (isEligibleToAddFunds || isEligibleToLinkBankAccount) {
      return generatePath(`${commonPath}move-money`, {
        businessEntityUuid
      });
    }

    return '';
  }, [
    businessEntityUuid,
    accountHasLinkedBankAccount,
    accountHasPayment,
    accountHasTransaction,
    bankingPath
  ]);

  const value = useMemoObject({
    data: {
      accountDetails,
      addFundModalHref,
      availableBalance,
      isEmptyTransaction,
      transactions
    },
    loading,
    error,
    refetch
  });

  return value;
};
